import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { repository } from '../data'
import type { MeaningLang, Profile, SavedWord, SubtitleMode, TopikLevel } from '../lib/types'

interface ToastState {
  message: string
  actionLabel?: string
  actionTo?: string
}

interface AppState {
  profile: Profile | null
  loading: boolean
  error: string | null
  savedWords: SavedWord[]
  /** 지금 복습할 수 있는 단어 수 — 탭바 '복습' 배지 */
  reviewDue: number
  /** 데이터 소스 — 'local' 이면 Demo mode 배너 노출 */
  mode: 'supabase' | 'local'
  toast: ToastState | null

  setLevel(level: TopikLevel): Promise<void>
  /** 단어 뜻풀이 언어 (스토리보드 UI 3-1 "다국어 번역") */
  setMeaningLang(lang: MeaningLang | null): Promise<void>
  /** 자막 번역 표시 방식 */
  setSubtitleMode(mode: SubtitleMode): Promise<void>
  refreshSavedWords(): Promise<void>
  /** 복습을 마치고 돌아올 때 배지를 다시 센다 */
  refreshReviewDue(): Promise<void>
  saveWord(input: Omit<SavedWord, 'id' | 'createdAt' | 'mastered'>): Promise<boolean>
  deleteSavedWord(id: string): Promise<void>
  setWordMastered(id: string, mastered: boolean): Promise<void>
  showToast(toast: ToastState): void
  dismissToast(): void
}

const AppContext = createContext<AppState | null>(null)

const TOAST_MS = 2600

export function AppProvider({ children }: { children: ReactNode }) {
  const [profile, setProfile] = useState<Profile | null>(null)
  const [savedWords, setSavedWords] = useState<SavedWord[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [toast, setToast] = useState<ToastState | null>(null)
  const [reviewDue, setReviewDue] = useState(0)
  const toastTimer = useRef<number | undefined>(undefined)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const [p, words] = await Promise.all([
          repository.getProfile(),
          repository.listSavedWords(),
        ])
        if (cancelled) return
        setProfile(p)
        setSavedWords(words)
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : '데이터를 불러오지 못했습니다.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  // 배지는 있으면 좋은 정보다 — 실패해도 화면 전체를 에러로 덮지 않고 0 으로 둔다.
  const refreshReviewDue = useCallback(async () => {
    try {
      const today = await repository.getStudyToday()
      setReviewDue(today.due)
    } catch {
      /* 무시 */
    }
  }, [])

  useEffect(() => {
    void refreshReviewDue()
  }, [refreshReviewDue])

  useEffect(() => () => window.clearTimeout(toastTimer.current), [])

  const showToast = useCallback((next: ToastState) => {
    window.clearTimeout(toastTimer.current)
    setToast(next)
    toastTimer.current = window.setTimeout(() => setToast(null), TOAST_MS)
  }, [])

  const dismissToast = useCallback(() => {
    window.clearTimeout(toastTimer.current)
    setToast(null)
  }, [])

  const setLevel = useCallback(async (level: TopikLevel) => {
    const next = await repository.updateProfile({ topikLevel: level })
    setProfile(next)
  }, [])

  const setMeaningLang = useCallback(async (lang: MeaningLang | null) => {
    const next = await repository.updateProfile({ meaningLang: lang })
    setProfile(next)
  }, [])

  const setSubtitleMode = useCallback(async (mode: SubtitleMode) => {
    const next = await repository.updateProfile({ subtitleMode: mode })
    setProfile(next)
  }, [])

  const refreshSavedWords = useCallback(async () => {
    setSavedWords(await repository.listSavedWords())
  }, [])

  const saveWord = useCallback(
    async (input: Omit<SavedWord, 'id' | 'createdAt' | 'mastered'>) => {
      const { saved, duplicate } = await repository.saveWord(input)
      if (!duplicate) {
        setSavedWords((prev) => [saved, ...prev])
        // 방금 저장한 단어는 복습 대기에 바로 들어간다 (review_due 가 비어 있음).
        setReviewDue((n) => n + 1)
      }
      void repository.markStudyDay()
      return duplicate
    },
    [],
  )

  /** 암기 완료 토글. 미암기가 위, 암기 완료가 아래로 간다. */
  const setWordMastered = useCallback(
    async (id: string, mastered: boolean) => {
      await repository.setWordMastered(id, mastered)
      if (mastered) void repository.markStudyDay()
      setSavedWords((prev) =>
        [...prev.map((w) => (w.id === id ? { ...w, mastered } : w))].sort(
          (a, b) => Number(a.mastered) - Number(b.mastered),
        ),
      )
      // 암기 완료한 단어는 복습에서 빠진다 — 되돌리면 다시 들어온다.
      void refreshReviewDue()
    },
    [refreshReviewDue],
  )

  const deleteSavedWord = useCallback(
    async (id: string) => {
      await repository.deleteSavedWord(id)
      setSavedWords((prev) => prev.filter((w) => w.id !== id))
      void refreshReviewDue()
    },
    [refreshReviewDue],
  )

  const value = useMemo<AppState>(
    () => ({
      profile,
      loading,
      error,
      savedWords,
      reviewDue,
      mode: repository.mode,
      toast,
      setLevel,
      setMeaningLang,
      setSubtitleMode,
      refreshSavedWords,
      refreshReviewDue,
      saveWord,
      deleteSavedWord,
      setWordMastered,
      showToast,
      dismissToast,
    }),
    [
      profile,
      loading,
      error,
      savedWords,
      reviewDue,
      toast,
      setLevel,
      setMeaningLang,
      setSubtitleMode,
      refreshSavedWords,
      refreshReviewDue,
      saveWord,
      deleteSavedWord,
      setWordMastered,
      showToast,
      dismissToast,
    ],
  )

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>
}

export function useApp(): AppState {
  const ctx = useContext(AppContext)
  if (!ctx) throw new Error('useApp must be used within <AppProvider>')
  return ctx
}
